/**
 * The "Reads as" choice (Option C C3, ruling after review r5): a string several engines read alike (`result.choices` —
 * nothing tells them apart) is neither refused nor guessed. The user picks the engine; the string is parsed again with it
 * (`ParseOptions.engine`), and that reading is the one previewed and applied. A pick belongs to the text it was made for:
 * other text starts over. `feature:connection_string_engine_chosen` fires on each pick that changes it — the engine only.
 */
import { useMemo, useState } from 'react';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { parseConnectionString, type ConnectionStringParseResult } from '../../services/connection-string/parseConnectionString';

export type ReadsAs = {
  /** The engines to choose from; undefined when the string is not such a string. */
  choices: DatabaseEngine[] | undefined;
  picked: DatabaseEngine | null;
  /** The result to preview and apply: the picked engine's reading, else `result` itself. */
  result: ConnectionStringParseResult | null;
  choose: (engine: DatabaseEngine) => void;
};

export function useReadsAs(text: string, result: ConnectionStringParseResult | null, track: TrackFunction): ReadsAs {
  const [pick, setPick] = useState<{ text: string; engine: DatabaseEngine } | null>(null);
  const choices = result?.choices;
  const picked = pick && choices && pick.text === text && choices.includes(pick.engine) ? pick.engine : null;
  const chosen = useMemo(() => (picked ? parseConnectionString(text, { engine: picked }) : null), [text, picked]);
  return {
    choices,
    picked,
    result: chosen ?? result,
    choose: (engine) => {
      if (engine === picked) return;
      setPick({ text, engine });
      track('feature:connection_string_engine_chosen', { engine });
    },
  };
}
